import type React from "react";
import { useMemo, useState } from "react";
import { BookOpen, ChevronDown } from "lucide-react";
import type { DimensionId } from "@benyishiguang/ui-foundation";
import type { ApiSubskill } from "@/api/growth";
import type { Practice } from "../types";
import { DIMENSIONS } from "../dimensions";
import { getFeaturedPracticesByDimension } from "../selectors";
import { EmptyState } from "@/components/growth/EmptyState";
import { PracticeCard } from "@/components/growth/PracticeCard";
const isDevBuild = import.meta.env.DEV;
const PRACTICE_ART_SRC = "/brand/practice-art.png";
const PRACTICE_EMPTY_DEV_TEXT =
	"题库为空。请检查 DATABASE_URL；请核验迁移状态，不要重新初始化存量库。";
const PRACTICE_EMPTY_PROD_TEXT = "练习题库正在准备中，稍后再来看看。";
export const PracticeScreen: React.FC<{
	activeIds: string[];
	isLoading: boolean;
	loadError: string | null;
	practices: Practice[];
	subskills: ApiSubskill[];
	onOpenPractice: (id: string) => void;
	onToggleOngoing: (id: string) => void;
}> = ({ activeIds, isLoading, loadError, onOpenPractice, onToggleOngoing, practices }) => {
	const [expandedDimensions, setExpandedDimensions] = useState<DimensionId[]>(() =>
		DIMENSIONS.map((dimension) => dimension.id),
	);
	const hasPracticeCatalog = practices.length > 0;
	const emptyTitle = isLoading
		? "正在从数据库加载题库"
		: loadError
			? "数据库题库没有加载成功"
			: hasPracticeCatalog
				? "精选练习正在准备中"
				: "数据库题库还是空的";
	const emptyText = isLoading
		? "题库来自 Postgres 的 exercises 表，加载完成后会按五类展示。"
		: loadError
			? loadError
			: hasPracticeCatalog
				? "后台暂时没有配置这个分类的精选练习，请稍后再来看看。"
				: isDevBuild
					? PRACTICE_EMPTY_DEV_TEXT
					: PRACTICE_EMPTY_PROD_TEXT;
	const practiceGroups = useMemo(
		() =>
			DIMENSIONS.map((dimension) => ({
				dimension,
				practices: getFeaturedPracticesByDimension(practices, dimension.id),
			})).filter((group) => group.practices.length > 0),
		[practices],
	);
	const toggleDimension = (dimensionId: DimensionId) => {
		setExpandedDimensions((current) =>
			current.includes(dimensionId)
				? current.filter((id) => id !== dimensionId)
				: [...current, dimensionId],
		);
	};

	return (
		<section className="app-screen practice-screen">
			<header className="plain-header">
				<div>
					<h1>练习</h1>
					<p className="screen-subtitle">从一个具体动作开始，慢慢看见什么对你有效。</p>
				</div>
				<BookOpen size={24} />
			</header>
			<section className="model-card">
				<div>
					<strong>五维积极成长模型</strong>
					<h2>现在最需要哪一种支持？</h2>
					<p>觉察、优势、联结、韧性、希望，每次只选一个小行动。</p>
				</div>
				<img alt="" aria-hidden="true" className="model-card-art" src={PRACTICE_ART_SRC} />
			</section>
			{practiceGroups.length === 0 ? (
				<EmptyState
					className="practice-bank-empty"
					icon={BookOpen}
					text={emptyText}
					title={emptyTitle}
					tone="strength"
				/>
			) : (
				<div className="practice-groups">
					{practiceGroups.map(({ dimension, practices: groupPractices }) => {
						const expanded = expandedDimensions.includes(dimension.id);
						const orderedPractices = [...groupPractices].sort(
							(left, right) =>
								Number(activeIds.includes(right.id)) - Number(activeIds.includes(left.id)),
						);
						const visiblePractices = expanded ? orderedPractices : orderedPractices.slice(0, 1);
						const canExpand = orderedPractices.length > 1;
						return (
							<section className="practice-group" key={dimension.id}>
								<button
									aria-expanded={expanded}
									className="practice-group-head"
									disabled={!canExpand}
									onClick={() => toggleDimension(dimension.id)}
									type="button"
								>
									<span className="practice-group-icon">
										<dimension.Icon size={16} />
									</span>
									<span className="practice-group-copy">
										<strong>{dimension.name}</strong>
										<small>{dimension.blurb}</small>
									</span>
									<span className="practice-group-count">
										{groupPractices.length} 个{canExpand && <ChevronDown size={17} />}
									</span>
								</button>
								<div className="practice-group-cards">
									{visiblePractices.map((practice) => {
										const active = activeIds.includes(practice.id);
										return (
											<PracticeCard
												key={practice.id}
												id={practice.id}
												title={practice.title}
												subtitle={practice.sub}
												active={active}
												canShare={Boolean(practice.share)}
												hasAssessment={Boolean(practice.assess)}
												onOpen={onOpenPractice}
												onToggle={onToggleOngoing}
											/>
										);
									})}
								</div>
							</section>
						);
					})}
				</div>
			)}
		</section>
	);
};
